/**
 * Orchestra AI Platform - API Node Component
 * 
 * Custom node component for API endpoint calls
 * Matches the styling of APICard from the toolbox
 * 
 * @fileoverview API node with server badge, method, status, and handles
 */

import { Handle, Position } from 'reactflow';
import PropTypes from 'prop-types';
import { ServerBadge } from '../../ui/ServerBadge';
import { MethodBadge } from '../../ui/MethodBadge';
import '../../Toolbox/APICard.css';

/**
 * @typedef {Object} APINodeData
 * @property {string} id
 * @property {string} label
 * @property {string} [name] - API name (used if label not available)
 * @property {string} [description] - API description
 * @property {('chinese'|'english'|'maths'|'myclass')} server
 * @property {string} endpoint
 * @property {('GET'|'POST'|'PUT'|'DELETE')} method
 * @property {Object} parameters
 * @property {Object} configuration
 * @property {('idle'|'running'|'success'|'error')} [status]
 * @property {*} [lastResult]
 * @property {string} [error]
 */

/**
 * Status indicator component
 * @param {Object} props
 * @param {string} props.status
 */
function StatusIndicator({ status }) {
  const statusConfig = {
    idle: { color: 'bg-gray-400', icon: '○' },
    running: { color: 'bg-blue-500 animate-pulse', icon: '⟳' },
    success: { color: 'bg-green-500', icon: '✓' },
    error: { color: 'bg-red-500', icon: '✗' },
  };

  const config = statusConfig[status] || statusConfig.idle;

  return (
    <div className={`api-node-status w-2 h-2 rounded-full ${config.color} flex items-center justify-center text-white text-[10px]`}>
      {config.icon}
    </div>
  );
}

StatusIndicator.propTypes = {
  status: PropTypes.oneOf(['idle', 'running', 'success', 'error']).isRequired,
};

/**
 * API Node Component
 * Matches the styling and structure of APICard from toolbox
 * @param {Object} props
 * @param {APINodeData} props.data
 * @param {boolean} props.selected
 */
export function APINode({ data, selected }) {
  const displayName = data.name || data.label || data.endpoint;
  const displayDescription = data.description || '';

  return (
    <div
      className={`api-card api-node ${
        selected ? 'selected' : ''
      } ${data.status === 'error' ? 'border-red-500' : ''}`}
    >
      <Handle type="target" position={Position.Left} className="w-3 h-3 !bg-gray-400" />

      {/* Header - matches APICard header */}
      <div className="api-card-header">
        <div className="api-card-badges">
          <ServerBadge server={data.server} size="sm" />
          <MethodBadge method={data.method} size="sm" />
        </div>
        {data.status && data.status !== 'idle' && (
          <StatusIndicator status={data.status} />
        )}
      </div>

      {/* Content - matches APICard content */}
      <h3 className="api-card-title" title={displayName}>
        {displayName}
      </h3>
      {displayDescription && (
        <p className="api-card-description" title={displayDescription}>
          {displayDescription}
        </p>
      )}

      {/* Endpoint - matches APICard endpoint */}
      <code className="api-card-endpoint" title={data.endpoint}>
        {data.endpoint}
      </code>

      {/* Error message if present */}
      {data.status === 'error' && data.error && (
        <div className="text-xs text-red-600 mt-1 truncate" title={data.error}>
          {data.error}
        </div>
      )}

      <Handle type="source" position={Position.Right} className="w-3 h-3 !bg-gray-400" />
    </div>
  );
}

APINode.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    server: PropTypes.oneOf(['chinese', 'english', 'maths', 'myclass']).isRequired,
    endpoint: PropTypes.string.isRequired,
    method: PropTypes.oneOf(['GET', 'POST', 'PUT', 'DELETE']).isRequired,
    parameters: PropTypes.object,
    configuration: PropTypes.object,
    status: PropTypes.oneOf(['idle', 'running', 'success', 'error']),
    lastResult: PropTypes.any,
    error: PropTypes.string,
  }).isRequired,
  selected: PropTypes.bool,
};

